import React from 'react';
import { Typography, Box, Tooltip } from '@common-ui/ui';
import imageStatusNamesEnum from '../../../enums/imageStatusNamesEnum';
import { useAppDispatch } from '../../../store/configureStore';
import { getISOStringFromDate } from '../../../utils/getDateFormat';

import {
  updateReportLocation,
  updateReportEquipment,
  updateReportRecommendation,
} from '../../../store/slices/reportSlice';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@common-ui/ui';

import {
  CheckCircle,
  ErrorOutline,
  CancelOutlined,
  SkipNextOutlined,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import './styles.css';
import TextField from '../../../components/InputFields/TextField';

export default function ReportDetailContent({ details, disabled = false }) {
  const dispatch = useAppDispatch();

  const headers = ['Date', 'Location', 'Equipment', 'Status', 'Reason'];

  const {
    image_id,
    name,
    date_taken,
    site_id,
    equipment,
    recommendation,
    status_code = 0,
    status_text = '',
    reason_text = '',
  } = details || {};

  const getStatus = (status) => {
    const response = Object.keys(imageStatusNamesEnum)[status];
    if (response === 'NoState') return 'No Status Selected';
    else return response;
  };

  const StatusIcon = ({ status }) => {
    let color = getStatus(status);

    switch (color) {
      case '1':
        return <CheckCircle style={{ color: '#28a745' }} />;
      case '2':
        return <ErrorOutline style={{ color: '#ffc107' }} />;
      case '3':
        return <CancelOutlined style={{ color: '#dc3545' }} />;
      case '4':
        return <SkipNextOutlined style={{ color: '#007bff' }} />;
      default:
        return <RadioButtonUnchecked style={{ color: 'gray' }} />;
    }
  };

  const styles = {
    tableTextField: {
      fullWidth: true,
      InputProps: { disableUnderline: true },
      sx: {
        backgroundColor: 'grey.200',
        under: 'none',
        borderRadius: '4px',
      },
    },

    tableHeader: {
      borderLeft: '1px solid gray',
      borderBottom: '1px solid gray',
      backgroundColor: 'grey.300',
    },

    tableCell: {
      borderLeft: '1px solid gray',
      borderBottom: '1px solid gray',
    },
  };

  return (
    <Box
      id={`reportDetail-${image_id}`}
      className="reportsection-textfield-container"
      sx={{ backgroundColor: 'background.paper' }}
    >
      <Box id="imageName" className="reportsection-row">
        <Typography variant="h4">{name}</Typography>
        <Tooltip title={status_text}>
          <div>
            <StatusIcon status={status_code} />
          </div>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} sx={{ border: '1px solid gray' }}>
        <Table size="small" aria-label="report table">
          <TableHead>
            <TableRow>
              {headers.map((header, index) => (
                <TableCell key={`header_index_${index}`} sx={styles.tableHeader}>
                  <Typography> {header}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow className="row-bold">
              <TableCell style={styles.tableCell}>
                <Typography id="imageDate">{getISOStringFromDate(date_taken) ?? ''}</Typography>
              </TableCell>

              <TableCell style={styles.tableCell}>
                <TextField
                  id="site_id"
                  variant="standard"
                  initialValue={site_id ?? ''}
                  onBlur={(e) => dispatch(updateReportLocation(image_id, e.target.value))}
                  disabled={disabled}
                  {...styles.tableTextField}
                />
              </TableCell>

              <TableCell style={styles.tableCell}>
                <TextField
                  id="Equipment"
                  variant="standard"
                  initialValue={equipment ?? ''}
                  onBlur={(e) => dispatch(updateReportEquipment(image_id, e.target.value))}
                  disabled={disabled}
                  {...styles.tableTextField}
                />
              </TableCell>

              <TableCell className="text-center" style={styles.tableCell}>
                <Tooltip title={status_text}>
                  <div>
                    <StatusIcon status={status_code} />
                  </div>
                </Tooltip>
              </TableCell>

              <TableCell style={styles.tableCell}>
                <Typography id="reason">{reason_text}</Typography>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell colSpan={headers.length}>
                <Typography id="label-recommendation">Recommendation</Typography>
                <TextField
                  id="Recommendation"
                  initialValue={recommendation ?? ''}
                  multiline
                  rows={1}
                  onBlur={(e) => dispatch(updateReportRecommendation(image_id, e.target.value))}
                  disabled={disabled}
                  fullWidth
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
